import { User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ModuleKey } from '@/utils/modules';
import { useSidePanel } from './SidePanelContext';
import { ContactPanelView } from './views/ContactPanelView';

/**
 * Registry der Panel-Ansichten. Ein neuer Mieter = eine Zeile hier plus
 * eine View-Datei unter views/ — die Shell kennt keine Views direkt.
 *
 * Kontrakt: Views sind routing-agnostisch; sie bekommen `close()` und
 * entscheiden selbst, was damit passiert (z. B. Tab schließen nach
 * Löschen). Modul-Gating deklariert die View über `module`.
 */

export type PanelViewParams = Record<string, unknown>;

/**
 * Lose Params an der Registry-Grenze (heterogene Map) — die typsichere
 * Außenseite sind die openXxxPanel-Hooks unten; die View castet/validiert
 * ihre Params selbst.
 */
export interface PanelViewDef {
  /** Tab-Titel (Deutsch); bei open() berechnet und am Tab gespeichert. */
  title: (params: PanelViewParams) => string;
  icon: LucideIcon;
  /** Modul-Gate (utils/modules.ts); ohne → kein Gate. */
  module?: ModuleKey;
  /** Stabiler Identitäts-Key für Tab-Dedupe, unabhängig von kosmetischen Params (z. B. Name). */
  tabKey: (params: PanelViewParams) => string;
  Component: React.ComponentType<{ params: PanelViewParams; close: () => void }>;
}

export const PANEL_VIEWS = {
  contact: {
    title: (p) => (typeof p.name === 'string' && p.name ? p.name : '') || 'Kontakt',
    icon: User,
    module: 'kontakte' as ModuleKey,
    tabKey: (p) => `contact:${p.id}`,
    Component: ContactPanelView,
  },
  // rechnungen: { ... } ← zukünftiger Mieter (Rechnungen-Modul): eine Zeile + eine View-Datei.
} satisfies Record<string, PanelViewDef>;

export type PanelViewKey = keyof typeof PANEL_VIEWS;

/** Öffnet einen Kontakt als Tab in der Seitenleiste (Dedupe über die Kontakt-ID). */
export function useOpenContactPanel() {
  const { open } = useSidePanel();
  return (id: number, name?: string) => {
    open('contact', { id, name }, { tabKey: `contact:${id}`, title: name || 'Kontakt' });
  };
}